import { inject } from '@angular/core'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router'
import { LoggerService } from '@danangelus/ngx-app-core'
import { User } from "../../models/user.model";
import { SessionService } from './session.service'

const log: LoggerService = LoggerService.get('Guards')

/**
 * Util class providing reusable route guards
 *
 * @author Dan Bennett
 */
export class Guards {

    public static isLoggedInButNotVerified = async (): Promise<boolean> => {

        const router = inject(Router)
        const sessionService = inject(SessionService)
        if (!await sessionService.isSessionActive()) {
            await router.navigate(['login'])
            return false
        }

        const user = await sessionService.getUser()
        if (user?.verified) {
            await router.navigate(['home'])
            return false
        }
        return true
    }

    public static isLoggedInAndVerified = async (): Promise<boolean> => {
        const router = inject(Router)
        const sessionService = inject(SessionService)
        const sessionActive = await sessionService.isSessionActive()
        if (!sessionActive) {
            sessionService.invalidateSession()
            await router.navigate(['login'])
            return false
        }
        const user = await sessionService.getUser()
        if (!user) {
            await router.navigate(['login'])
            return false
        }
        if (!user?.verified) {
            await router.navigate(['mfa'])
            return false
        }
        if (user?.missingDefaultMfa) {
            log.info('isLoggedInAndVerified', 'Default MFA not set. Redirecting to First-time setup.')
            await router.navigate(['account', 'first-time-mfa'])
            return false
        }
        return true
    }

    public static notLoggedIn = async (): Promise<boolean> => {

        const router = inject(Router)
        const sessionService = inject(SessionService)
        if (!await sessionService.isSessionActive()) {
            return true
        }
        await router.navigate(['home'])
        return false
    }

    public static hasDefaultMfa = async (): Promise<boolean> => {
        const sessionService = inject(SessionService)
        const user = await sessionService.getUser()

        return user?.missingDefaultMfa === true
    }

    public static hasGroup(group: string) {
        return async (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> => {
            const router = inject(Router)
            const sessionService = inject(SessionService)
            const user = await sessionService.getUser()

            log.debug('canActivate', `Checking user groups: ${user?.groups} for: ${state.url}`)
            if (await this.isUserActive(router, sessionService, user, state.url) && user!.groups.indexOf(group) > -1) {
                return true
            }
            await router.navigate(['home'])
            return false
        }
    }

    public static hasGroups(...groups: string[]) {
        return async (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> => {
            const router = inject(Router)
            const sessionService = inject(SessionService)
            const user = await sessionService.getUser()

            log.debug('canActivate', `Checking user groups: ${user?.groups} for: ${state.url}`)
            if (await this.isUserActive(router, sessionService, user, state.url) && groups.every(group => user!.groups.indexOf(group) > -1)) {
                return true
            }
            await router.navigate(['home'])
            return false
        }
    }

    public static hasAnyGroup(...groups: string[]) {
        return async (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> => {
            const router = inject(Router)
            const sessionService = inject(SessionService)
            const user = await sessionService.getUser()

            if (await this.isUserActive(router, sessionService, user, state.url) && groups.some(group => user!.groups.indexOf(group) > -1)) {
                return true
            }
            await router.navigate(['home'])
            return false
        }
    }

    private static async isUserActive(router: Router, sessionService: SessionService, user: User | null, url: string): Promise<boolean> {
        log.debug('isUserActive', `Checking if user still has an active session: ${user?.userId} for: ${url}`)

        if (!user || !await sessionService.isSessionActive()) {
            await router.navigate(['login'])
            return false
        }
        return true
    }

    public static passwordExpired = async (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> => {
        const router = inject(Router)
        const passwordExpired = await Guards.isPasswordExpired(router, state.url)
        if (passwordExpired && state.url.indexOf('password-expired') > -1) {
            return true
        } else if (passwordExpired) {
            await router.navigate(['account', 'password-expired'])
        } else {
            await router.navigate(['home'])
        }
        return false
    }

    public static passwordNotExpired = async (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> => {
        const router = inject(Router)
        const passwordExpired = await Guards.isPasswordExpired(router, state.url)
        if (passwordExpired) {
            await router.navigate(['account', 'password-expired'])
            return false
        }
        return true
    }

    private static async isPasswordExpired(router: Router, url: string) {

        const sessionService = inject(SessionService)
        const user = await sessionService.getUser()
        log.debug('canActivate', `Checking password expiry for: ${url}`)

        if (!user || !await sessionService.isSessionActive()) {
            await router.navigate(['login'])

        }
        return user?.passwordExpired || false
    }
}
